import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  User, 
  Briefcase, 
  GraduationCap, 
  Languages, 
  FileText,
  Plus,
  Trash2,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Check,
  Target,
  Globe,
  Award,
  Zap
} from 'lucide-react';
import { getApplicantProfile, updateApplicantProfile } from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LIFE_PHASES = {
  orientation: { name: "Orientierungsphase", sub: ["schueler", "praktikum", "berufsorientierung", "ausbildungsplatz", "duales_studium"] },
  education: { name: "Ausbildungs- & Studienphase", sub: ["azubi_de", "azubi_international_ausland", "azubi_international_de", "studierende", "dual_studierende"] },
  international: { name: "Internationale Fachkräfte", sub: ["fachkraft_ausland", "fachkraft_deutschland", "anerkennung_pending", "visa_pending"] },
  helper: { name: "Helfer- & Anlerntätigkeiten", sub: ["helfer", "anlernkraft", "perspektive_fachkraft"] },
  career: { name: "Karrierephase", sub: ["fachkraft", "senior", "fuehrungskraft", "branchenwechsel"] },
  redeployment: { name: "Redeployment", sub: ["interne_mobilitaet", "umschulung", "restrukturierung"] },
  senior_55plus: { name: "55+ Modelle", sub: ["altersteilzeit", "teilzeit_55plus", "minijob_55plus", "projektarbeit_55plus", "mentor"] },
  retirement: { name: "Rentenphase", sub: ["projektberatung", "expertenpool", "esg_programme"] }
};

const SUB_PHASE_LABELS = {
  schueler: "Schüler", praktikum: "Praktikum", berufsorientierung: "Berufsorientierung",
  ausbildungsplatz: "Ausbildungsplatz suchen", duales_studium: "Duales Studium",
  azubi_de: "Azubi (Deutschland)", azubi_international_ausland: "Internationale Azubis (Ausland)",
  azubi_international_de: "Internationale Azubis (DE)", studierende: "Studierende", dual_studierende: "Dual Studierende",
  fachkraft_ausland: "Fachkraft im Ausland", fachkraft_deutschland: "Fachkraft in Deutschland",
  anerkennung_pending: "Anerkennung ausstehend", visa_pending: "Visum ausstehend",
  helfer: "Helfer", anlernkraft: "Anlernkraft", perspektive_fachkraft: "Perspektive Fachkraft",
  fachkraft: "Fachkraft", senior: "Senior", fuehrungskraft: "Führungskraft", branchenwechsel: "Branchenwechsel",
  interne_mobilitaet: "Interne Mobilität", umschulung: "Umschulung", restrukturierung: "Restrukturierung",
  altersteilzeit: "Altersteilzeit", teilzeit_55plus: "Teilzeit 55+", minijob_55plus: "Minijob 55+",
  projektarbeit_55plus: "Projektarbeit 55+", mentor: "Mentor/Wissenstransfer",
  projektberatung: "Projektberatung", expertenpool: "Expertenpool", esg_programme: "ESG-Programme"
};

const COMPETENCY_CATEGORIES = [
  { value: "fachlich", label: "Fachlich" },
  { value: "methodisch", label: "Methodisch" },
  { value: "sozial", label: "Sozial" },
  { value: "digital", label: "Digital" },
  { value: "fuehrung", label: "Führung" }
];

const WORK_MODELS = [
  { value: "vor_ort", label: "Vor Ort" },
  { value: "hybrid", label: "Hybrid" },
  { value: "remote", label: "Remote" },
  { value: "projektbasiert", label: "Projektbasiert" },
  { value: "teilzeit", label: "Teilzeit" },
  { value: "minijob", label: "Minijob" },
  { value: "schicht", label: "Schichtarbeit" },
  { value: "altersteilzeit", label: "Altersteilzeit" }
];

const RECOGNITION_CLUSTERS = [
  { value: "aerzte", label: "Ärzte" },
  { value: "apotheker", label: "Apotheker" },
  { value: "pflege", label: "Pflegeberufe" },
  { value: "physiotherapie", label: "Physiotherapie/Ergotherapie" },
  { value: "hebammen", label: "Hebammen" },
  { value: "lehrer_erzieher", label: "Lehrer/Erzieher" },
  { value: "ingenieure", label: "Ingenieure/Architekten" },
  { value: "handwerk", label: "Handwerksmeister" }
];

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [profile, setProfile] = useState({
    first_name: '', last_name: '', phone: '', address: '', city: '',
    postal_code: '', country: 'Deutschland', date_of_birth: '', nationality: '',
    life_phase: 'career', life_phase_detail: 'fachkraft',
    job_title: '', desired_job: '', desired_salary: '', availability: '',
    work_models_preferred: [], willing_to_relocate: false,
    competencies: [], work_experience: [], education: [], languages: [],
    skills: [], industries_preferred: [],
    recognition_status: null, visa_status: null, german_level: '',
    ausbildung_info: null, about_me: '', anonymized: true
  });

  const steps = [
    { title: 'Lebensphase', icon: Target },
    { title: 'Persönliche Daten', icon: User },
    { title: 'Kompetenzen', icon: Award },
    { title: 'Berufserfahrung', icon: Briefcase },
    { title: 'Ausbildung', icon: GraduationCap },
    { title: 'Sprachen', icon: Languages },
    { title: 'Präferenzen', icon: Zap }
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await getApplicantProfile();
      setProfile({ ...profile, ...res.data });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateApplicantProfile(profile);
      setProfile({ ...profile, ...res.data });
      toast.success('Profil gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    await handleSave();
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Competency handlers
  const addCompetency = () => {
    setProfile({
      ...profile,
      competencies: [...profile.competencies, { 
        competency_id: `comp_${Date.now()}`,
        name: '', category: 'fachlich', level: 3, years_experience: 0, verified: false, source: 'selbst'
      }]
    });
  };

  const removeCompetency = (index) => {
    setProfile({
      ...profile,
      competencies: profile.competencies.filter((_, i) => i !== index)
    });
  };

  const updateCompetency = (index, field, value) => {
    const updated = [...profile.competencies];
    updated[index] = { ...updated[index], [field]: value };
    setProfile({ ...profile, competencies: updated });
  };

  // Work experience handlers
  const addWorkExperience = () => {
    setProfile({
      ...profile,
      work_experience: [...profile.work_experience, { 
        position: '', company: '', industry: '', start_date: '', end_date: '', 
        description: '', tasks: [], competencies_used: []
      }]
    });
  };

  const removeWorkExperience = (index) => {
    setProfile({ ...profile, work_experience: profile.work_experience.filter((_, i) => i !== index) });
  };

  const updateWorkExperience = (index, field, value) => {
    const updated = [...profile.work_experience];
    updated[index] = { ...updated[index], [field]: value };
    setProfile({ ...profile, work_experience: updated });
  };

  // Education handlers
  const addEducation = () => {
    setProfile({
      ...profile,
      education: [...profile.education, { 
        degree: '', field: '', institution: '', start_date: '', end_date: '', country: 'Deutschland', recognized: true
      }]
    });
  };

  const removeEducation = (index) => {
    setProfile({ ...profile, education: profile.education.filter((_, i) => i !== index) });
  };

  const updateEducation = (index, field, value) => {
    const updated = [...profile.education];
    updated[index] = { ...updated[index], [field]: value };
    setProfile({ ...profile, education: updated });
  };

  // Language handlers
  const addLanguage = () => {
    setProfile({
      ...profile,
      languages: [...profile.languages, { language: '', level: 'B1', certified: false, certificate: '' }]
    });
  };

  const removeLanguage = (index) => {
    setProfile({ ...profile, languages: profile.languages.filter((_, i) => i !== index) });
  };

  const updateLanguage = (index, field, value) => {
    const updated = [...profile.languages];
    updated[index] = { ...updated[index], [field]: value };
    setProfile({ ...profile, languages: updated });
  };

  const handleSkillsChange = (e) => {
    const skills = e.target.value.split(',').map(s => s.trim()).filter(s => s);
    setProfile({ ...profile, skills });
  };

  const toggleWorkModel = (model) => {
    const current = profile.work_models_preferred || [];
    if (current.includes(model)) {
      setProfile({ ...profile, work_models_preferred: current.filter(m => m !== model) });
    } else {
      setProfile({ ...profile, work_models_preferred: [...current, model] });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Kompetenzprofil</h1>
          <p className="text-muted-foreground mt-1">Ihr persönliches Kompetenz- und Lebensphasen-Profil</p>
        </div>

        {/* Progress */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Profil-Vollständigkeit</span>
              <span className="text-sm font-bold text-primary">{profile.profile_complete}%</span>
            </div>
            <Progress value={profile.profile_complete} className="h-2" />
          </CardContent>
        </Card>

        {/* Step Navigation */}
        <div className="flex items-center justify-between bg-muted/50 rounded-lg p-2 overflow-x-auto">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap ${
                currentStep === index
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <step.icon className="w-4 h-4" />
              <span className="hidden md:inline text-sm font-medium">{step.title}</span>
            </button>
          ))}
        </div>

        {/* Step Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {React.createElement(steps[currentStep].icon, { className: "w-5 h-5 text-primary" })}
              {steps[currentStep].title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Step 0: Life Phase */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>In welcher Lebensphase befinden Sie sich?</Label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {Object.entries(LIFE_PHASES).map(([key, value]) => (
                      <button
                        key={key}
                        onClick={() => setProfile({ ...profile, life_phase: key, life_phase_detail: value.sub[0] })}
                        className={`p-4 rounded-lg border text-left transition-all ${
                          profile.life_phase === key
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <span className="font-medium">{value.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {profile.life_phase && LIFE_PHASES[profile.life_phase] && (
                  <div className="space-y-2">
                    <Label>Genauer Status</Label>
                    <Select 
                      value={profile.life_phase_detail} 
                      onValueChange={(v) => setProfile({ ...profile, life_phase_detail: v })}
                    >
                      <SelectTrigger data-testid="life-phase-detail">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LIFE_PHASES[profile.life_phase].sub.map(sub => (
                          <SelectItem key={sub} value={sub}>
                            {SUB_PHASE_LABELS[sub] || sub}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Recognition Status for International */}
                {profile.life_phase === 'international' && (
                  <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Anerkennungsstatus
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Berufsgruppe</Label>
                        <Select 
                          value={profile.recognition_status?.cluster || ''} 
                          onValueChange={(v) => setProfile({ 
                            ...profile, 
                            recognition_status: { ...profile.recognition_status, cluster: v }
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Wählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            {RECOGNITION_CLUSTERS.map(c => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select 
                          value={profile.recognition_status?.status || 'nicht_begonnen'} 
                          onValueChange={(v) => setProfile({ 
                            ...profile, 
                            recognition_status: { ...profile.recognition_status, status: v }
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nicht_begonnen">Nicht begonnen</SelectItem>
                            <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                            <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Anonymization Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <h4 className="font-medium">Anonymisiertes Profil</h4>
                    <p className="text-sm text-muted-foreground">
                      Arbeitgeber sehen zunächst keine persönlichen Daten
                    </p>
                  </div>
                  <Switch 
                    checked={profile.anonymized} 
                    onCheckedChange={(v) => setProfile({ ...profile, anonymized: v })}
                    data-testid="anonymized-toggle"
                  />
                </div>
              </div>
            )}

            {/* Step 1: Personal Data */}
            {currentStep === 1 && (
              <div className="grid gap-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vorname</Label>
                    <Input
                      value={profile.first_name}
                      onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                      data-testid="profile-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nachname</Label>
                    <Input
                      value={profile.last_name}
                      onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                      data-testid="profile-last-name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Telefon</Label>
                    <Input
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nationalität</Label>
                    <Input
                      value={profile.nationality}
                      onChange={(e) => setProfile({ ...profile, nationality: e.target.value })}
                      placeholder="z.B. Deutsch"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input
                    value={profile.address}
                    onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>PLZ</Label>
                    <Input
                      value={profile.postal_code}
                      onChange={(e) => setProfile({ ...profile, postal_code: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stadt</Label>
                    <Input
                      value={profile.city}
                      onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Land</Label>
                    <Input
                      value={profile.country}
                      onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Über mich</Label>
                  <Textarea
                    value={profile.about_me}
                    onChange={(e) => setProfile({ ...profile, about_me: e.target.value })}
                    placeholder="Erzählen Sie etwas über sich..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Competencies */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Fügen Sie Ihre Kompetenzen hinzu und bewerten Sie Ihr Level (1-5)
                </p>

                {profile.competencies.map((comp, index) => (
                  <Card key={index} className="relative">
                    <Button
                      variant="ghost" size="icon"
                      className="absolute top-2 right-2 text-destructive hover:text-destructive"
                      onClick={() => removeCompetency(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Kompetenz</Label>
                          <Input
                            value={comp.name}
                            onChange={(e) => updateCompetency(index, 'name', e.target.value)}
                            placeholder="z.B. Projektmanagement"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Kategorie</Label>
                          <Select 
                            value={comp.category} 
                            onValueChange={(v) => updateCompetency(index, 'category', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {COMPETENCY_CATEGORIES.map(c => (
                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Level: {comp.level}/5</Label>
                          <span className="text-sm text-muted-foreground">
                            {comp.level === 1 && "Grundkenntnisse"}
                            {comp.level === 2 && "Anwender"}
                            {comp.level === 3 && "Fortgeschritten"}
                            {comp.level === 4 && "Profi"}
                            {comp.level === 5 && "Experte"}
                          </span>
                        </div>
                        <Slider
                          value={[comp.level]}
                          onValueChange={(v) => updateCompetency(index, 'level', v[0])}
                          min={1} max={5} step={1}
                          className="w-full"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Jahre Erfahrung</Label>
                          <Input
                            type="number"
                            value={comp.years_experience}
                            onChange={(e) => updateCompetency(index, 'years_experience', parseFloat(e.target.value) || 0)}
                            min={0} step={0.5}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Quelle</Label>
                          <Select 
                            value={comp.source} 
                            onValueChange={(v) => updateCompetency(index, 'source', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="selbst">Selbsteinschätzung</SelectItem>
                              <SelectItem value="zertifikat">Zertifikat</SelectItem>
                              <SelectItem value="arbeitgeber">Arbeitgeberbestätigung</SelectItem>
                              <SelectItem value="test">Test/Assessment</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button variant="outline" onClick={addCompetency} className="w-full rounded-lg" data-testid="add-competency-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Kompetenz hinzufügen
                </Button>

                {/* Skills Tags */}
                <div className="space-y-2">
                  <Label>Weitere Fähigkeiten (kommagetrennt)</Label>
                  <Textarea
                    value={profile.skills.join(', ')}
                    onChange={handleSkillsChange}
                    placeholder="z.B. JavaScript, React, Scrum, Vertrieb..."
                    rows={3}
                  />
                  {profile.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {profile.skills.map((skill, idx) => (
                        <Badge key={idx} variant="secondary">{skill}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Work Experience */}
            {currentStep === 3 && (
              <div className="space-y-6">
                {profile.work_experience.map((exp, index) => (
                  <Card key={index} className="relative">
                    <Button
                      variant="ghost" size="icon"
                      className="absolute top-2 right-2 text-destructive"
                      onClick={() => removeWorkExperience(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Position</Label>
                          <Input
                            value={exp.position}
                            onChange={(e) => updateWorkExperience(index, 'position', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Unternehmen</Label>
                          <Input
                            value={exp.company}
                            onChange={(e) => updateWorkExperience(index, 'company', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Branche</Label>
                          <Input
                            value={exp.industry}
                            onChange={(e) => updateWorkExperience(index, 'industry', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Von</Label>
                          <Input
                            type="month"
                            value={exp.start_date}
                            onChange={(e) => updateWorkExperience(index, 'start_date', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Bis</Label>
                          <Input
                            type="month"
                            value={exp.end_date}
                            onChange={(e) => updateWorkExperience(index, 'end_date', e.target.value)}
                            placeholder="oder 'heute'"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Beschreibung</Label>
                        <Textarea
                          value={exp.description}
                          onChange={(e) => updateWorkExperience(index, 'description', e.target.value)}
                          rows={3}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button variant="outline" onClick={addWorkExperience} className="w-full rounded-lg">
                  <Plus className="w-4 h-4 mr-2" />
                  Berufserfahrung hinzufügen
                </Button>
              </div>
            )}

            {/* Step 4: Education */}
            {currentStep === 4 && (
              <div className="space-y-6">
                {profile.education.map((edu, index) => (
                  <Card key={index} className="relative">
                    <Button
                      variant="ghost" size="icon"
                      className="absolute top-2 right-2 text-destructive"
                      onClick={() => removeEducation(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Abschluss</Label>
                          <Input
                            value={edu.degree}
                            onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Fachrichtung</Label>
                          <Input
                            value={edu.field}
                            onChange={(e) => updateEducation(index, 'field', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Institution</Label>
                          <Input
                            value={edu.institution}
                            onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Land</Label>
                          <Input
                            value={edu.country}
                            onChange={(e) => updateEducation(index, 'country', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Von</Label>
                          <Input type="month" value={edu.start_date}
                            onChange={(e) => updateEducation(index, 'start_date', e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Bis</Label>
                          <Input type="month" value={edu.end_date}
                            onChange={(e) => updateEducation(index, 'end_date', e.target.value)} />
                        </div>
                      </div>
                      {edu.country !== 'Deutschland' && (
                        <div className="flex items-center gap-2">
                          <Switch 
                            checked={edu.recognized} 
                            onCheckedChange={(v) => updateEducation(index, 'recognized', v)}
                          />
                          <Label>Abschluss in Deutschland anerkannt</Label>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                <Button variant="outline" onClick={addEducation} className="w-full rounded-lg">
                  <Plus className="w-4 h-4 mr-2" />
                  Ausbildung hinzufügen
                </Button>
              </div>
            )}

            {/* Step 5: Languages */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>Deutschkenntnisse</Label>
                  <Select 
                    value={profile.german_level} 
                    onValueChange={(v) => setProfile({ ...profile, german_level: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A1">A1 - Anfänger</SelectItem>
                      <SelectItem value="A2">A2 - Grundkenntnisse</SelectItem>
                      <SelectItem value="B1">B1 - Fortgeschritten</SelectItem>
                      <SelectItem value="B2">B2 - Fließend</SelectItem>
                      <SelectItem value="C1">C1 - Verhandlungssicher</SelectItem>
                      <SelectItem value="C2">C2 - Muttersprachlich</SelectItem>
                      <SelectItem value="Muttersprache">Muttersprache</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {profile.languages.map((lang, index) => (
                  <Card key={index} className="relative">
                    <Button
                      variant="ghost" size="icon"
                      className="absolute top-2 right-2 text-destructive"
                      onClick={() => removeLanguage(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <CardContent className="pt-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Sprache</Label>
                          <Input
                            value={lang.language}
                            onChange={(e) => updateLanguage(index, 'language', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Niveau</Label>
                          <Select value={lang.level} onValueChange={(v) => updateLanguage(index, 'level', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="A1">A1</SelectItem>
                              <SelectItem value="A2">A2</SelectItem>
                              <SelectItem value="B1">B1</SelectItem>
                              <SelectItem value="B2">B2</SelectItem>
                              <SelectItem value="C1">C1</SelectItem>
                              <SelectItem value="C2">C2</SelectItem>
                              <SelectItem value="Muttersprache">Muttersprache</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button variant="outline" onClick={addLanguage} className="w-full rounded-lg">
                  <Plus className="w-4 h-4 mr-2" />
                  Sprache hinzufügen
                </Button>
              </div>
            )}

            {/* Step 6: Preferences */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Gewünschte Position</Label>
                    <Input
                      value={profile.desired_job}
                      onChange={(e) => setProfile({ ...profile, desired_job: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Verfügbarkeit</Label>
                    <Select value={profile.availability} onValueChange={(v) => setProfile({ ...profile, availability: v })}>
                      <SelectTrigger><SelectValue placeholder="Wählen..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sofort">Sofort</SelectItem>
                        <SelectItem value="2_wochen">In 2 Wochen</SelectItem>
                        <SelectItem value="1_monat">In 1 Monat</SelectItem>
                        <SelectItem value="3_monate">In 3 Monaten</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Bevorzugte Arbeitsmodelle</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {WORK_MODELS.map(model => (
                      <button
                        key={model.value}
                        onClick={() => toggleWorkModel(model.value)}
                        className={`p-3 rounded-lg border text-sm transition-all ${
                          profile.work_models_preferred?.includes(model.value)
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        {model.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <h4 className="font-medium">Umzugsbereit</h4>
                    <p className="text-sm text-muted-foreground">Sind Sie bereit, für einen Job umzuziehen?</p>
                  </div>
                  <Switch 
                    checked={profile.willing_to_relocate} 
                    onCheckedChange={(v) => setProfile({ ...profile, willing_to_relocate: v })}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={handlePrev} disabled={currentStep === 0} className="rounded-full">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>

          {currentStep < steps.length - 1 ? (
            <Button onClick={handleNext} disabled={saving} className="rounded-full">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Weiter
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={saving} className="rounded-full">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Check className="w-4 h-4 mr-2" />
              Profil speichern
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
